/**
 * Supabase PostgREST Data Service
 *
 * Provides CRUD operations via Supabase PostgREST as an alternative to Express API routes.
 * Feature-flagged: only active when REACT_APP_USE_SUPABASE_DATA=true AND Supabase auth is enabled.
 *
 * Returns data in axios-compatible format: { data: ... } so frontend code works unchanged.
 */
import supabase, { isSupabaseAuthEnabled } from './supabase';

export const isSupabaseDataEnabled = () => {
  return process.env.REACT_APP_USE_SUPABASE_DATA === 'true' && isSupabaseAuthEnabled();
};

// Helper: wrap Supabase response to match axios format { data: ... }
function wrapResponse(supabaseResult) {
  if (supabaseResult.error) {
    const err = new Error(supabaseResult.error.message);
    err.response = { status: supabaseResult.status, data: { error: supabaseResult.error.message } };
    throw err;
  }
  return { data: supabaseResult.data };
}

// ── Blocked Senders ──────────────────────────────────

export const supabaseBlockedSendersAPI = {
  getAll: async (params = {}) => {
    let query = supabase
      .from('blocked_senders')
      .select('*, users:blocked_by_user_id(first_name, last_name, email)', { count: 'exact' });

    if (params.search) {
      query = query.or(`phone_number.ilike.%${params.search}%,reason.ilike.%${params.search}%`);
    }

    const page = parseInt(params.page) || 1;
    const limit = parseInt(params.limit) || 50;
    const offset = (page - 1) * limit;

    query = query.order('blocked_at', { ascending: false }).range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) throw Object.assign(new Error(error.message), { response: { status: 400, data: { error: error.message } } });

    // Flatten joined user data to match Express format
    const formatted = (data || []).map(row => ({
      ...row,
      blocked_by_name: row.users ? `${row.users.first_name || ''} ${row.users.last_name || ''}`.trim() : null,
      blocked_by_email: row.users?.email || null,
      users: undefined
    }));

    return {
      data: {
        blockedSenders: formatted,
        pagination: { page, limit, total: count || 0, totalPages: Math.ceil((count || 0) / limit) }
      }
    };
  },

  block: async (data) => {
    // Get current user info from Supabase session
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.user_metadata?.legacy_id;

    const result = await supabase
      .from('blocked_senders')
      .insert({
        phone_number: data.phone_number,
        reason: data.reason || null,
        blocked_by_user_id: userId,
        tenant_id: session?.user?.user_metadata?.tenant_id
      })
      .select()
      .single();

    return wrapResponse(result);
  },

  unblock: async (id) => {
    const result = await supabase.from('blocked_senders').delete().eq('id', id);
    return wrapResponse(result);
  },

  checkPhone: async (phone) => {
    const { data, error } = await supabase
      .from('blocked_senders')
      .select('id')
      .eq('phone_number', phone)
      .maybeSingle();

    if (error) throw Object.assign(new Error(error.message), { response: { status: 400, data: { error: error.message } } });
    return { data: { isBlocked: !!data } };
  }
};

// ── Tags ─────────────────────────────────────────────

export const supabaseTagsAPI = {
  getAll: async () => {
    const { data, error } = await supabase
      .from('tags')
      .select('*, conversation_tags(id)')
      .order('name');

    if (error) throw Object.assign(new Error(error.message), { response: { status: 400, data: { error: error.message } } });

    // Add usage_count from joined conversation_tags
    const formatted = (data || []).map(tag => ({
      ...tag,
      usage_count: tag.conversation_tags?.length || 0,
      conversation_tags: undefined
    }));

    return { data: { tags: formatted } };
  },

  getById: async (id) => {
    const result = await supabase
      .from('tags')
      .select('*, conversation_tags(id)')
      .eq('id', id)
      .single();

    if (result.error) return wrapResponse(result);

    const tag = {
      ...result.data,
      usage_count: result.data.conversation_tags?.length || 0,
      conversation_tags: undefined
    };

    return { data: tag };
  },

  getConversationTags: async (conversationId) => {
    const { data, error } = await supabase
      .from('conversation_tags')
      .select('*, tags(*)')
      .eq('conversation_id', conversationId);

    if (error) throw Object.assign(new Error(error.message), { response: { status: 400, data: { error: error.message } } });

    // Flatten to return tag objects
    const tags = (data || []).map(ct => ct.tags).filter(Boolean);
    return { data: tags };
  },

  create: async (data) => {
    const { data: { session } } = await supabase.auth.getSession();
    const result = await supabase
      .from('tags')
      .insert({
        name: data.name,
        color: data.color || '#3B82F6',
        tenant_id: session?.user?.user_metadata?.tenant_id,
        created_by_user_id: session?.user?.user_metadata?.legacy_id
      })
      .select()
      .single();
    return wrapResponse(result);
  },

  update: async (id, data) => {
    const result = await supabase
      .from('tags')
      .update({ name: data.name, color: data.color })
      .eq('id', id)
      .select()
      .single();
    return wrapResponse(result);
  },

  delete: async (id) => {
    // Cascade: conversation_tags FK has ON DELETE CASCADE
    const result = await supabase.from('tags').delete().eq('id', id);
    return wrapResponse(result);
  }
};

// ── Templates (message_templates) ────────────────────

export const supabaseTemplatesAPI = {
  getAll: async (params = {}) => {
    let query = supabase
      .from('message_templates')
      .select('*, users:created_by_user_id(first_name, last_name)');

    if (params.category) {
      query = query.eq('category', params.category);
    }

    if (params.search) {
      query = query.or(`name.ilike.%${params.search}%,content.ilike.%${params.search}%,shortcut.ilike.%${params.search}%`);
    }

    // Filter shared templates or own templates. RLS enforces the outer
    // tenant boundary (see migration 049 — super_admin bypass dropped
    // from message_templates) but does NOT enforce per-user privacy
    // within a tenant, so add it explicitly. Default view: shared
    // templates for the tenant + the caller's own private templates.
    // Named params still narrow further ('mine' only, 'shared' only).
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.user_metadata?.legacy_id;
    if (params.shared === 'true') {
      query = query.eq('is_shared', true);
    } else if (params.mine === 'true' && userId) {
      query = query.eq('created_by_user_id', userId);
    } else if (userId) {
      // Default: shared OR mine (matches the Express route at
      // routes/templates.js:22-23 for parity).
      query = query.or(`is_shared.eq.true,created_by_user_id.eq.${userId}`);
    }

    query = query.order('name');

    const { data, error } = await query;
    if (error) throw Object.assign(new Error(error.message), { response: { status: 400, data: { error: error.message } } });

    // Flatten joined user data
    const formatted = (data || []).map(t => ({
      ...t,
      created_by_name: t.users ? `${t.users.first_name || ''} ${t.users.last_name || ''}`.trim() : null,
      users: undefined
    }));

    return { data: { templates: formatted } };
  },

  getById: async (id) => {
    const result = await supabase
      .from('message_templates')
      .select('*, users:created_by_user_id(first_name, last_name)')
      .eq('id', id)
      .single();

    return wrapResponse(result);
  },

  getCategories: async () => {
    const { data, error } = await supabase
      .from('message_templates')
      .select('category')
      .not('category', 'is', null)
      .order('category');

    if (error) throw Object.assign(new Error(error.message), { response: { status: 400, data: { error: error.message } } });

    // Deduplicate
    const categories = [...new Set((data || []).map(r => r.category).filter(Boolean))];
    return { data: { categories } };
  },

  create: async (data) => {
    const { data: { session } } = await supabase.auth.getSession();
    const result = await supabase
      .from('message_templates')
      .insert({
        name: data.name,
        content: data.content,
        category: data.category || 'General',
        shortcut: data.shortcut || null,
        is_shared: data.is_shared || false,
        // media_urls persists a JSON array (matches messages.media_urls
        // shape) so the composer can attach template media on send.
        media_urls: Array.isArray(data.media_urls)
          ? JSON.stringify(data.media_urls)
          : (data.media_urls || null),
        tenant_id: session?.user?.user_metadata?.tenant_id,
        created_by_user_id: session?.user?.user_metadata?.legacy_id
      })
      .select()
      .single();
    return wrapResponse(result);
  },

  update: async (id, data) => {
    const updates = {};
    if (data.name !== undefined) updates.name = data.name;
    if (data.content !== undefined) updates.content = data.content;
    if (data.category !== undefined) updates.category = data.category;
    if (data.shortcut !== undefined) updates.shortcut = data.shortcut;
    if (data.is_shared !== undefined) updates.is_shared = data.is_shared;
    if (data.media_urls !== undefined) {
      updates.media_urls = Array.isArray(data.media_urls)
        ? JSON.stringify(data.media_urls)
        : (data.media_urls || null);
    }

    const result = await supabase
      .from('message_templates')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return wrapResponse(result);
  },

  delete: async (id) => {
    const result = await supabase.from('message_templates').delete().eq('id', id);
    return wrapResponse(result);
  }
};

// ── Opt-out / Allow Lists ────────────────────────────

export const supabaseListsAPI = {
  getOptOutList: async (params = {}) => {
    let query = supabase
      .from('opt_out_list')
      .select('*', { count: 'exact' });

    if (params.search) {
      query = query.ilike('phone_number', `%${params.search}%`);
    }

    const page = parseInt(params.page) || 1;
    const limit = parseInt(params.limit) || 50;
    const offset = (page - 1) * limit;

    query = query.order('opted_out_at', { ascending: false }).range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) throw Object.assign(new Error(error.message), { response: { status: 400, data: { error: error.message } } });

    return {
      data: {
        opted_out: data || [],
        pagination: { page, limit, total: count || 0, totalPages: Math.ceil((count || 0) / limit) }
      }
    };
  },

  getAllowList: async (params = {}) => {
    let query = supabase
      .from('allow_list')
      .select('*', { count: 'exact' });

    if (params.search) {
      query = query.ilike('phone_number', `%${params.search}%`);
    }

    const page = parseInt(params.page) || 1;
    const limit = parseInt(params.limit) || 50;
    const offset = (page - 1) * limit;

    query = query.order('created_at', { ascending: false }).range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) throw Object.assign(new Error(error.message), { response: { status: 400, data: { error: error.message } } });

    return {
      data: {
        allowed: data || [],
        pagination: { page, limit, total: count || 0, totalPages: Math.ceil((count || 0) / limit) }
      }
    };
  },

  addToOptOutList: async (data) => {
    const { data: { session } } = await supabase.auth.getSession();
    const result = await supabase
      .from('opt_out_list')
      .insert({
        phone_number: data.phone_number,
        reason: data.reason || 'manual',
        notes: data.notes || null,
        contact_id: data.contact_id || null,
        added_by_user_id: session?.user?.user_metadata?.legacy_id,
        tenant_id: session?.user?.user_metadata?.tenant_id
      })
      .select()
      .single();
    return wrapResponse(result);
  },

  removeFromOptOutList: async (id) => {
    const result = await supabase.from('opt_out_list').delete().eq('id', id);
    return wrapResponse(result);
  },

  removeFromOptOutListByPhone: async (phone) => {
    const { data: { session } } = await supabase.auth.getSession();
    const tenantId = session?.user?.user_metadata?.tenant_id;
    let query = supabase.from('opt_out_list').delete().eq('phone_number', phone);
    if (tenantId) query = query.eq('tenant_id', tenantId);
    const result = await query;
    if (result.error) throw Object.assign(new Error(result.error.message), { response: { status: 400, data: { error: result.error.message } } });
    return { data: { success: true } };
  },

  addToAllowList: async (data) => {
    const { data: { session } } = await supabase.auth.getSession();
    const result = await supabase
      .from('allow_list')
      .insert({
        phone_number: data.phone_number,
        reason: data.reason || null,
        contact_id: data.contact_id || null,
        added_by_user_id: session?.user?.user_metadata?.legacy_id,
        tenant_id: session?.user?.user_metadata?.tenant_id
      })
      .select()
      .single();
    return wrapResponse(result);
  },

  removeFromAllowList: async (id) => {
    const result = await supabase.from('allow_list').delete().eq('id', id);
    return wrapResponse(result);
  }
};

// ── Contacts ─────────────────────────────────────────

export const supabaseContactsAPI = {
  getAll: async (params = {}) => {
    let query = supabase
      .from('contacts')
      .select('*', { count: 'exact' });

    // Filter by view
    if (params.view === 'company') {
      query = query.is('user_id', null);
    } else if (params.view === 'opt-out') {
      // Fetch opt-out phone numbers first, then filter contacts to only those
      const { data: optOutData } = await supabase.from('opt_out_list').select('phone_number');
      const optOutPhones = (optOutData || []).map(r => r.phone_number);
      if (optOutPhones.length === 0) {
        return { data: { contacts: [], pagination: { page: 1, limit: parseInt(params.limit) || 50, total: 0, totalPages: 0 } } };
      }
      query = query.in('phone_number', optOutPhones);
    } else if (!params.view) {
      // Default: show only current user's contacts
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.user_metadata?.legacy_id;
      if (userId) {
        query = query.eq('user_id', userId);
      }
    }

    if (params.search) {
      query = query.or(
        `phone_number.ilike.%${params.search}%,first_name.ilike.%${params.search}%,last_name.ilike.%${params.search}%,email.ilike.%${params.search}%,company.ilike.%${params.search}%`
      );
    }

    const page = parseInt(params.page) || 1;
    const limit = parseInt(params.limit) || 50;
    const offset = (page - 1) * limit;

    const sortBy = params.sortBy || 'created_at';
    const sortOrder = params.sortOrder === 'asc' ? true : false;
    query = query.order(sortBy, { ascending: sortOrder }).range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) throw Object.assign(new Error(error.message), { response: { status: 400, data: { error: error.message } } });

    // Fetch opt-out and allow list phone numbers to merge
    const [optOutRes, allowRes] = await Promise.all([
      supabase.from('opt_out_list').select('phone_number'),
      supabase.from('allow_list').select('phone_number')
    ]);
    const optOutPhones = new Set((optOutRes.data || []).map(r => r.phone_number));
    const allowPhones = new Set((allowRes.data || []).map(r => r.phone_number));

    const contacts = (data || []).map(c => ({
      ...c,
      is_opted_out: optOutPhones.has(c.phone_number),
      is_allowed: allowPhones.has(c.phone_number)
    }));

    return {
      data: {
        contacts,
        pagination: { page, limit, total: count || 0, totalPages: Math.ceil((count || 0) / limit) }
      }
    };
  },

  getById: async (id) => {
    const result = await supabase
      .from('contacts')
      .select('*')
      .eq('id', id)
      .single();
    return wrapResponse(result);
  },

  create: async (data) => {
    const { data: { session } } = await supabase.auth.getSession();
    const result = await supabase
      .from('contacts')
      .insert({
        phone_number: data.phone_number,
        name_prefix: data.name_prefix || null,
        first_name: data.first_name || null,
        last_name: data.last_name || null,
        suffix: data.suffix || null,
        email: data.email || null,
        company: data.company || null,
        title: data.title || null,
        notes: data.notes || null,
        tags: data.tags || null,
        custom_fields: data.custom_fields || null,
        custom_field_1: data.custom_field_1 || null,
        custom_field_2: data.custom_field_2 || null,
        custom_field_3: data.custom_field_3 || null,
        tenant_id: session?.user?.user_metadata?.tenant_id,
        user_id: session?.user?.user_metadata?.legacy_id
      })
      .select()
      .single();
    return wrapResponse(result);
  },

  update: async (id, data) => {
    const updates = {};
    const fields = ['phone_number', 'name_prefix', 'first_name', 'last_name', 'suffix', 'email', 'company', 'title', 'notes', 'tags', 'custom_fields', 'custom_field_1', 'custom_field_2', 'custom_field_3', 'opted_in'];
    for (const f of fields) {
      if (data[f] !== undefined) updates[f] = data[f];
    }

    const result = await supabase
      .from('contacts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return wrapResponse(result);
  },

  delete: async (id) => {
    const result = await supabase.from('contacts').delete().eq('id', id);
    return wrapResponse(result);
  }
};
